import React, { useState, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Collapse, Link, Stack, Avatar, Badge, Divider
} from '@mui/material';
import {
  Category as CategoryIcon,
  LocationOn,
  Email,
  Phone,
  CalendarToday,
  Visibility,
  ExpandMore,
  ExpandLess,
  Business,
  Public,
  Person,
  Image,
  CheckCircle,
  Cancel,
  MoreTime,
  Work,
  ContactMail
} from '@mui/icons-material';
import { updateServiceVisits } from '../../services/serviceService';
import { useAuth } from '../../context/authContext';

const OpportunityCard = ({ opportunity }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [visitsCount, setVisitsCount] = useState(opportunity?.visits || 0);
  const { user } = useAuth();
  const hasUpdatedVisits = useRef(false);

  const safeOpportunity = {
    id: opportunity?.id || 0,
    title: opportunity?.title || 'OPORTUNIDAD NO DISPONIBLE',
    description: opportunity?.description || 'NO HAY DESCRIPCIÓN DISPONIBLE',
    type_service: opportunity?.type_service || null,
    type_publish: opportunity?.type_publish || 'TIPO NO ESPECIFICADO',
    category: opportunity?.category || 'SIN CATEGORÍA',
    date_publish: opportunity?.date_publish ,
    contact: opportunity?.contact || 'N/A',
    email: opportunity?.email || 'N/A',
    location: opportunity?.location || 'UBICACIÓN NO ESPECIFICADA',
    visits: visitsCount,
    status: opportunity?.status || 'active',
    company_rut: opportunity?.company_rut || '',
    name_contact: opportunity?.name_contact || 'Contacto no especificado',
    image: opportunity?.image || null,
    company: opportunity?.company || {
      business_name: 'Empresa no especificada',
      trade_name: '',
      industry: 'No especificado',
      mobile_phone: '',
      web_page: '',
      logo: ''
    },
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'FECHA NO DISPONIBLE';
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(Number(year), Number(month) - 1, Number(day)); // sin hora
    return date.toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric' });
  };


  const handleToggle = async () => {
    if (!isExpanded && user && !hasUpdatedVisits.current) {
      try {
        setVisitsCount(prev => prev + 1);
        updateServiceVisits(safeOpportunity.id, user);
        hasUpdatedVisits.current = true;
      } catch (error) {
        console.error("Error updating visits:", error);
        setVisitsCount(prev => prev - 1);
      }
    }
    setIsExpanded(!isExpanded);
  };

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  // Enhanced color scheme
  const headerBg = 'linear-gradient(135deg, #09274A 0%, #0488AF 100%)';
  const highlightColor = '#0488AF';
  const statusColor = safeOpportunity.status === 'active' ? '#4CAF50' : '#F44336';
  
  // Color scheme for type_publish and type_service
  const getTypePublishColor = () => {
    if (safeOpportunity.type_publish === 'Servicio') return '#2196F3'; 
    if (safeOpportunity.type_publish === 'Bien/Producto') return '#4CAF50'; 
    return '#0488AF'; // Default blue
  };
  
  const getTypeServiceColor = () => {
    if (safeOpportunity.type_service === 'Oferta') return '#4CAF50'; 
    if (safeOpportunity.type_service === 'Demanda') return '#FFC107'; 
    return '#9E9E9E'; // Default gray
  };

  // Determinar si mostrar el type_service (solo para Servicio)
  const shouldShowTypeService = safeOpportunity.type_publish === 'Servicio' && safeOpportunity.type_service;

  return (
    <Box sx={{ 
      position: 'relative', 
      height: '100%',
      '&:hover': {
        '& .company-logo': {
          transform: 'scale(1.05)',
          boxShadow: '0 0 20px rgba(4, 136, 175, 0.4)'
        },
        '& .status-badge': {
          transform: 'scale(1.1)'
        }
      }
    }}>
      <Paper elevation={3} sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 25px rgba(9, 39, 74, 0.4)'
        }
      }}>
        {/* Header with enhanced styling */}
        <Box sx={{
          background: headerBg,
          p: 2,
          color: 'white',
          position: 'relative'
        }}>
          <Box sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            gap: 1
          }}>
            <Chip
              label={safeOpportunity.type_publish.toUpperCase()}
              size="small"
              sx={{
                backgroundColor: getTypePublishColor(),
                color: 'white',
                fontWeight: 'bold',
                height: '24px'
              }}
            />
            {/* Solo mostrar type_service si es Servicio */}
            {shouldShowTypeService && (
              <Chip
                label={safeOpportunity.type_service.toUpperCase()}
                size="small"
                sx={{
                  backgroundColor: getTypeServiceColor(),
                  color: 'white',
                  fontWeight: 'bold',
                  backdropFilter: 'blur(5px)',
                  height: '24px'
                }}
              />
            )}
          </Box>
          
          <Typography variant="h5" sx={{
            fontWeight: 800,
            fontSize: '1.4rem',
            pr: 6,
            lineHeight: 1.3,
            letterSpacing: '0.3px',
            textShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}>
            {safeOpportunity.title}
          </Typography>
          
          <Typography variant="caption" sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            opacity: 0.9,
            mt: 0.5
          }}>
            <MoreTime fontSize="small" />
            Publicado el {formatDate(safeOpportunity.date_publish)}
          </Typography>
        </Box>

        {/* Card body with enhanced layout */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flex: 1,
          p: 0
        }}>
          {/* Left side - Company info with image */}
          <Box sx={{
            width: { xs: '100%', md: '40%' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 3,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              height: '70%',
              width: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              display: { xs: 'none', md: 'block' }
            }
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flex: 1
            }}>
              <Avatar
                className="company-logo"
                src={safeOpportunity.company.logo || '/default-company-logo.png'}
                alt={safeOpportunity.company.business_name}
                sx={{
                  width: { xs: 120, md: 140 },
                  height: { xs: 120, md: 140 },
                  backgroundColor: 'white',
                  border: '3px solid #0488AF',
                  boxShadow: '0 0 0 5px rgba(4, 136, 175, 0.1)',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '& .MuiAvatar-img': {
                    objectFit: 'contain',
                    padding: '15px'
                  }
                }}
              />
              
              {safeOpportunity.image && (
                <Box sx={{
                  mt: 1,
                  textAlign: 'center'
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Image />}
                    onClick={() => window.open(safeOpportunity.image, '_blank')}
                    sx={{
                      borderColor: highlightColor,
                      color: highlightColor,
                      '&:hover': {
                        backgroundColor: 'rgba(4, 136, 175, 0.1)'
                      }
                    }}
                  >
                    Ver imagen adjunta
                  </Button>
                </Box>
              )}
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ 
                color: 'text.secondary',
                fontWeight: 600,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Work fontSize="small" />
                Tipo de publicación
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip 
                  label={safeOpportunity.type_publish} 
                  size="small"
                  sx={{ 
                    backgroundColor: getTypePublishColor(),
                    color: 'white',
                    fontWeight: 600,
                    borderRadius: '6px'
                  }}
                />
                {/* Solo mostrar type_service si es Servicio */}
                {shouldShowTypeService && (
                  <Chip 
                    label={safeOpportunity.type_service} 
                    size="small"
                    sx={{ 
                      backgroundColor: getTypeServiceColor(),
                      color: 'white',
                      fontWeight: 600,
                      borderRadius: '6px'
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Box>

          {/* Right side - Main content */}
          <Box sx={{
            width: { xs: '100%', md: '60%' },
            p: 3,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Company information */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600,
                color: '#09274A',
                mb: 0.5,
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Business sx={{ color: highlightColor, fontSize: '1.2rem' }} />
                {safeOpportunity.company.business_name || safeOpportunity.company.trade_name}
              </Typography>
            </Box>

            {/* Description with enhanced styling */}
            <Box sx={{
              flex: 1,
              mb: 2,
              position: 'relative',
              backgroundColor: 'rgba(249, 249, 251, 0.8)',
              borderRadius: '8px',
              p: 2,
              border: '1px solid rgba(4, 136, 175, 0.1)'
            }}>
              <Typography 
                variant="body1" 
                onClick={toggleDescription}
                sx={{
                  fontSize: '0.95rem',
                  lineHeight: 1.7,
                  color: 'text.primary',
                  whiteSpace: 'pre-line',
                  display: '-webkit-box',
                  WebkitLineClamp: showFullDescription ? 'unset' : 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: 'text.secondary'
                  }
                }}
              >
                {safeOpportunity.description}
              </Typography>

              {!showFullDescription && safeOpportunity.description.length > 150 && (
                <Button 
                  onClick={toggleDescription}
                  size="small"
                  sx={{ 
                    mt: 1,
                    color: highlightColor,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    '&:hover': {
                      backgroundColor: 'rgba(4, 136, 175, 0.1)'
                    }
                  }}
                >
                  Leer {showFullDescription ? 'menos' : 'más'}
                </Button>
              )}
            </Box>

            {/* Additional information with enhanced layout */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={safeOpportunity.location} 
                size="small"
                icon={<LocationOn fontSize="small" sx={{ color: highlightColor }} />}
                sx={{ 
                  backgroundColor: 'rgba(4, 136, 175, 0.1)',
                  color: highlightColor,
                  fontWeight: 600,
                  borderRadius: '8px'
                }}
              />
              <Chip 
                label={safeOpportunity.name_contact} 
                size="small"
                icon={<Person fontSize="small" sx={{ color: highlightColor }} />}
                sx={{ 
                  backgroundColor: 'rgba(4, 136, 175, 0.1)',
                  color: highlightColor,
                  fontWeight: 600,
                  borderRadius: '8px'
                }}
              />
            </Stack>

            {/* Visits counter with enhanced styling */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              backgroundColor: 'rgba(249, 249, 251, 0.8)',
              borderRadius: '8px',
              p: 1,
              border: '1px solid rgba(4, 136, 175, 0.1)'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                <Visibility fontSize="small" color="action" />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {visitsCount} visualizaciones
                </Typography>
              </Box>
              
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                {safeOpportunity.status === 'active' ? (
                  <CheckCircle fontSize="small" sx={{ color: statusColor }} />
                ) : (
                  <Cancel fontSize="small" sx={{ color: statusColor }} />
                )}
                <Typography variant="caption" sx={{ 
                  fontWeight: 600,
                  color: statusColor
                }}>
                  {safeOpportunity.status === 'active' ? 'Activo' : 'Inactivo'}
                </Typography>
              </Box>
            </Box>

            {/* Contact button with enhanced styling */}
            <Box sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, rgba(9, 39, 74, 0.9) 0%, rgba(4, 136, 175, 0.9) 100%)',
              boxShadow: '0 4px 15px rgba(4, 136, 175, 0.3)',
              transition: 'all 0.4s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(4, 136, 175, 0.4)'
              }
            }}>
              <Button
                variant="contained"
                fullWidth
                endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                onClick={handleToggle}
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  py: 1.2,
                  background: 'transparent',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  color: 'white',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'transparent',
                  }
                }}
              >
                {isExpanded ? 'Ocultar detalles' : 'Ver información de contacto'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Enhanced contact panel */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit
        sx={{
          position: 'absolute',
          left: '12px',
          right: '12px',
          bottom: '72px',
          zIndex: 2,
          '& .MuiCollapse-wrapper': {
            backgroundColor: 'background.paper',
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(9, 39, 74, 0.2)',
            border: `2px solid ${highlightColor}`,
            overflow: 'hidden',
            backdropFilter: 'blur(8px)',
            background: 'rgba(255,255,255,0.97)'
          }
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{
            mb: 2,
            fontWeight: 700,
            color: '#09274A',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontSize: '1.1rem'
          }}>
            <ContactMail sx={{ color: highlightColor }} />
            Información de contacto
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <Business fontSize="small" />
                  Empresa
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  pl: 2
                }}>
                  {safeOpportunity.company.business_name || safeOpportunity.company.trade_name}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <Person fontSize="small" />
                  Persona de contacto
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  pl: 2
                }}>
                  {safeOpportunity.name_contact}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <Phone fontSize="small" />
                  Teléfono
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  pl: 2
                }}>
                  {safeOpportunity.contact}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <Email fontSize="small" />
                  Email
                </Typography>
                <Link 
                  href={`mailto:${safeOpportunity.email}`} 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    pl: 2,
                    color: highlightColor,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {safeOpportunity.email}
                </Link>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <Public fontSize="small" />
                  Sitio web
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  pl: 2
                }}>
                  {safeOpportunity.company.web_page ? (
                    <Link 
                      href={safeOpportunity.company.web_page.includes('http') ? 
                            safeOpportunity.company.web_page : 
                            `https://${safeOpportunity.company.web_page}`} 
                      target="_blank" 
                      rel="noopener" 
                      sx={{ 
                        color: highlightColor,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {safeOpportunity.company.web_page.replace(/(^\w+:|^)\/\//, '')}
                    </Link>
                  ) : 'No especificado'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <LocationOn fontSize="small" />
                  Ubicación
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  pl: 2
                }}>
                  {safeOpportunity.location}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {safeOpportunity.image && (
            <Box sx={{ 
              mt: 2,
              pt: 2,
              borderTop: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Image />}
                onClick={() => window.open(safeOpportunity.image, '_blank')}
                sx={{
                  backgroundColor: highlightColor,
                  '&:hover': {
                    backgroundColor: '#09274A'
                  }
                }}
              >
                Ver imagen adjunta
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default OpportunityCard;